/**
 * AddTraslado.js — Formulario para trasladar fondos entre dos cuentas bancarias.
 * Genera un asiento tipo TRASLADO + dos movimientos bancarios:
 *   HABER → banco origen (sale)   ·   DEBE → banco destino (entra)
 *
 * Multimoneda: si origen y destino tienen monedas distintas, se pide el tipo de
 * cambio y el monto que entra se calcula por conversión pura (sin ganancia/pérdida).
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { SearchSelect } from "../../../../shared/components/SearchSelect";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import addStyles     from "../../invoices/styles/addInvoice.module.css";
import { useTipoCambio }  from "../../services/useTipoCambio";

const r2   = (n) => Math.round(Number(n) * 100) / 100;
const fmtN = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const sim  = (m) => (m === "USD" ? "$" : "₡");

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

// Input/textarea con un ícono a la izquierda — mismo lenguaje visual que el
// resto de los formularios de Add* (Recibos, Facturas), que usan campos con
// ícono en vez de inputs planos.
function IconInput({ icon, style, ...props }) {
  return (
    <div style={{ position:"relative" }}>
      <i className={`fa-solid ${icon}`} style={{ position:"absolute", left:12, top:"50%",
        transform:"translateY(-50%)", color:"var(--text-muted)", fontSize:"0.8rem", pointerEvents:"none" }}/>
      <input {...props} className="form-control" style={{ paddingLeft:32, ...style }}/>
    </div>
  );
}

function IconTextarea({ icon, style, ...props }) {
  return (
    <div style={{ position:"relative" }}>
      <i className={`fa-solid ${icon}`} style={{ position:"absolute", left:12, top:13,
        color:"var(--text-muted)", fontSize:"0.8rem", pointerEvents:"none" }}/>
      <textarea {...props} className="form-control"
        style={{ paddingLeft:32, resize:"vertical", minHeight:74, ...style }}/>
    </div>
  );
}

function FormTraslado({ onCancel, trasladoId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editMode = !!trasladoId;
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

  const [form, setForm] = useState({
    fecha:       today,
    referencia:  "",
    detalle:     "",
    monto:       "",
    cuentaOrigen:  null,
    cuentaDestino: null,
    tipo_cambio: 1,
  });

  // Ajuste manual del monto que ENTRA en destino — el banco a veces redondea
  // distinto al TC exacto (ej. reporta ₡387,000.00 en vez de ₡386,286.20).
  const [ajusteManual,      setAjusteManual]      = useState(false);
  const [montoDestinoAjust, setMontoDestinoAjust] = useState("");

  const [bancos,  setBancos]  = useState([]);
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [apiErr,  setApiErr]  = useState("");
  const [success, setSuccess] = useState(null);
  const [prefilling, setPrefilling] = useState(editMode);

  const { tipoCambio, loading: loadingTC } = useTipoCambio();

  // Monto destino guardado en el traslado (para detectar si fue ajustado a mano)
  const [montoDestinoGuardado, setMontoDestinoGuardado] = useState(null);

  // ── Modo edición: precargar el traslado existente ──────────────────────────
  useEffect(() => {
    if (!trasladoId) return;
    apiFetch(`/api/finance/traslado/${trasladoId}/`)
      .then(r => r.json())
      .then(d => {
        if (d.error) { setApiErr(d.error); return; }
        setForm({
          fecha:         d.fecha || today,
          referencia:    d.referencia || "",
          detalle:       d.detalle || "",
          monto:         String(d.monto_origen ?? ""),
          cuentaOrigen:  d.cuenta_origen ?? null,
          cuentaDestino: d.cuenta_destino ?? null,
          tipo_cambio:   Number(d.tipo_cambio) || 1,
        });
        setMontoDestinoGuardado(d.monto_destino != null ? Number(d.monto_destino) : null);
      })
      .catch(() => setApiErr("Error al cargar el traslado"))
      .finally(() => setPrefilling(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trasladoId]);

  const fetchBancos = useCallback(() => {
    return apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setBancos(list.map(b => ({
          id:     b.id,
          nombre: `${b.banco} — ${b.numero_cuenta}`,
          saldo:  Number(b.saldo) || 0,
          moneda: (b.moneda || "CRC").toUpperCase(),
        })));
      })
      .catch(console.error);
  }, []);

  useEffect(() => { fetchBancos(); }, [fetchBancos]);

  const origen  = bancos.find(b => b.id === form.cuentaOrigen)  || null;
  const destino = bancos.find(b => b.id === form.cuentaDestino) || null;
  const cruce   = origen && destino && origen.moneda !== destino.moneda;

  // Auto-cargar TC del BCCR (compra) cuando hay cruce de monedas y el TC sigue en 1
  useEffect(() => {
    if (cruce && tipoCambio?.tipo_compra && (!form.tipo_cambio || form.tipo_cambio === 1)) {
      setForm(p => ({ ...p, tipo_cambio: Number(tipoCambio.tipo_compra) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cruce, tipoCambio]);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  const montoNum = Number(form.monto) || 0;
  const tc       = Number(form.tipo_cambio) || 1;

  // Monto que ENTRA en la cuenta destino — conversión pura por TC, antes de
  // cualquier ajuste manual.
  let montoDestinoCalc = montoNum;
  if (cruce && origen) {
    montoDestinoCalc = origen.moneda === "CRC" ? r2(montoNum / tc) : r2(montoNum * tc);
  }
  const montoDestinoAjustNum = Number(montoDestinoAjust) || 0;
  // Monto final que realmente entra — el ajustado a mano si el toggle está
  // activo y tiene un valor válido, si no el calculado por TC.
  const montoDestino = (cruce && ajusteManual && montoDestinoAjustNum > 0)
    ? montoDestinoAjustNum
    : montoDestinoCalc;
  const diferenciaAjuste = montoDestino - montoDestinoCalc;

  // Al editar un traslado que ya tenía el monto ajustado a mano, detectarlo y
  // precargar el toggle + el valor guardado en vez de recalcularlo desde cero.
  useEffect(() => {
    if (!editMode || montoDestinoGuardado == null || !cruce) return;
    if (Math.abs(montoDestinoGuardado - montoDestinoCalc) > 0.01) {
      setAjusteManual(true);
      setMontoDestinoAjust(String(montoDestinoGuardado));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, montoDestinoGuardado, cruce, origen, destino]);

  function resetForm() {
    setForm(p => ({
      ...p, referencia: "", detalle: "", monto: "",
      cuentaOrigen: null, cuentaDestino: null, tipo_cambio: 1,
    }));
    setAjusteManual(false); setMontoDestinoAjust("");
    setErrors({}); setApiErr("");
  }

  function validate() {
    const e = {};
    if (!form.fecha)                              e.fecha         = t("traslados.fechaReq");
    if (!form.cuentaOrigen)                       e.cuentaOrigen  = t("traslados.origenReq");
    if (!form.cuentaDestino)                      e.cuentaDestino = t("traslados.destinoReq");
    if (form.cuentaOrigen && form.cuentaDestino && form.cuentaOrigen === form.cuentaDestino)
      e.cuentaDestino = t("traslados.mismaCuenta");
    if (!form.monto || montoNum <= 0)             e.monto         = t("traslados.montoReq");
    // En edición el saldo lo valida el backend (contando la reversa del monto viejo);
    // aquí solo lo chequeamos al crear para no dar falsos negativos.
    if (!editMode && origen && montoNum > origen.saldo)
                                                  e.monto         = t("traslados.saldoInsuficiente", { saldo: `${sim(origen.moneda)}${fmtN(origen.saldo)}` });
    if (cruce && tc <= 1)                         e.tipo_cambio   = t("traslados.tcReq");
    if (cruce && ajusteManual && montoDestinoAjustNum <= 0)
                                                  e.montoDestinoAjust = t("traslados.montoAjustadoReq");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setApiErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiFetch(
        editMode ? `/api/finance/traslado/${trasladoId}/` : "/api/finance/traslado/",
        {
          method: editMode ? "PATCH" : "POST",
          body: JSON.stringify({
            fecha:          form.fecha,
            cuenta_origen:  form.cuentaOrigen,
            cuenta_destino: form.cuentaDestino,
            monto:          montoNum,
            tipo_cambio:    cruce ? tc : 1,
            monto_destino_manual: (cruce && ajusteManual && montoDestinoAjustNum > 0)
              ? montoDestinoAjustNum : null,
            referencia:     form.referencia.trim(),
            detalle:        form.detalle.trim(),
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.detail || "Error al guardar");
      }
      await res.json().catch(() => ({}));
      if (editMode) {
        navigate("/finanzas/traslados");   // volver a la lista tras editar
        return;
      }
      resetForm();
      fetchBancos();               // refrescar saldos tras el traslado
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  const errorCount = Object.keys(errors).length;

  if (prefilling) {
    return (
      <div style={{ padding:40, textAlign:"center", color:"var(--text-secondary)" }}>
        <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:8, color:"#60a5fa" }}/>
        {t("traslados.cargandoTraslado")}
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/traslados">
          <span className={invoiceStyles.inactive}> {t("traslados.titulo")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {editMode ? t("common.editar") : t("common.agregar")}</span>
      </span>

      <h2 style={{ margin:"10px 0 20px", fontSize:20, fontWeight:600, color:"var(--text-primary)",
        display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ background:"rgba(96,165,250,0.15)", color:"#60a5fa",
          padding:"6px 8px", borderRadius:8 }}>
          <i className={`fa-solid ${editMode ? "fa-pen" : "fa-right-left"}`} style={{ color:"#60a5fa" }}/>
        </span>
        {editMode ? t("traslados.editarTitulo") : t("traslados.nuevoTraslado")}
      </h2>

      {/* Toast de éxito */}
      {success && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:2000,
          background:"rgba(74,222,128,0.07)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px",
          display:"flex", alignItems:"center", gap:10,
          boxShadow:"0 8px 32px rgba(0,0,0,0.4)", maxWidth:380 }}>
          <i className="fa-solid fa-circle-check" style={{ color:"#4ade80", fontSize:"1.1rem" }}/>
          <span style={{ color:"#4ade80", fontWeight:600 }}>{t("traslados.trasladoOk")}</span>
        </div>
      )}

      {/* Errores */}
      {errorCount > 0 && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8,
            color:"#f87171", fontWeight:600, marginBottom:6 }}>
            <i className="fa-solid fa-triangle-exclamation"/>
            {t("common.hay")} {errorCount} {t("registros.camposConError")}
          </div>
          <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
            {Object.values(errors).map((m,i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px",
          marginBottom:16, color:"#f87171", fontSize:"0.88rem",
          display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={addStyles["invoice-container"]}>

          {/* ── Izquierda: campos ── */}
          <div className={addStyles["invoice-details"]}>
            <div className={addStyles["invoice-details-header"]}>
              <h1>
                <i className={`fa-solid fa-right-left ${addStyles["icon-invoice-details"]}`}/>
                {t("traslados.detallesTraslado")}
              </h1>
            </div>

            <div className={addStyles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* Fecha + Referencia */}
              <div className="row">
                <div className="col">
                  <label>{t("common.fecha")} <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha}
                    onChange={e => set("fecha", e.target.value)}
                    style={{ borderColor:errors.fecha ? "#ef4444" : undefined }}/>
                  <FieldError msg={errors.fecha}/>
                </div>
                <div className="col">
                  <label>{t("common.referencia")}</label>
                  <IconInput icon="fa-hashtag" type="text"
                    placeholder={t("registros.referenciaPlaceholder")}
                    value={form.referencia}
                    onChange={e => set("referencia", e.target.value)}/>
                </div>
              </div>

              {/* Cuenta Origen (HABER / sale) */}
              <div>
                <label>
                  {t("traslados.cuentaOrigen")} <span style={{ color:"#f87171" }}>*</span>
                  <span style={{ color:"#f87171", fontWeight:400, marginLeft:6, fontSize:"0.74rem" }}>
                    {t("traslados.saleBadge")}
                  </span>
                </label>
                <SearchSelect
                  icon="fa-building-columns"
                  options={bancos.filter(b => b.id !== form.cuentaDestino).map(b => ({
                    id: b.id, primary: b.nombre,
                    secondary: `${b.moneda} · ${sim(b.moneda)}${fmtN(b.saldo)}`,
                  }))}
                  value={form.cuentaOrigen}
                  onChange={id => set("cuentaOrigen", id)}
                  placeholder={t("traslados.seleccionaOrigen")}
                  error={errors.cuentaOrigen}
                />
              </div>

              {/* Cuenta Destino (DEBE / entra) */}
              <div>
                <label>
                  {t("traslados.cuentaDestino")} <span style={{ color:"#f87171" }}>*</span>
                  <span style={{ color:"#4ade80", fontWeight:400, marginLeft:6, fontSize:"0.74rem" }}>
                    {t("traslados.entraBadge")}
                  </span>
                </label>
                <SearchSelect
                  icon="fa-building-columns"
                  options={bancos.filter(b => b.id !== form.cuentaOrigen).map(b => ({
                    id: b.id, primary: b.nombre,
                    secondary: `${b.moneda} · ${sim(b.moneda)}${fmtN(b.saldo)}`,
                  }))}
                  value={form.cuentaDestino}
                  onChange={id => set("cuentaDestino", id)}
                  placeholder={t("traslados.seleccionaDestino")}
                  error={errors.cuentaDestino}
                />
              </div>

              {/* Monto (en la moneda de la cuenta origen) */}
              <div>
                <label>
                  {t("traslados.montoSale")} <span style={{ color:"#f87171" }}>*</span>
                  {origen && (
                    <span style={{ color:"var(--text-muted)", fontWeight:400, marginLeft:6, fontSize:"0.74rem" }}>
                      ({origen.moneda})
                    </span>
                  )}
                </label>
                <IconInput icon="fa-sack-dollar" type="number" min="0.01" step="0.01"
                  placeholder="0.00" value={form.monto}
                  onChange={e => set("monto", e.target.value)}
                  style={{ borderColor:errors.monto ? "#ef4444" : undefined }}/>
                <FieldError msg={errors.monto}/>
              </div>

              {/* Tipo de cambio — solo cuando origen y destino tienen monedas distintas */}
              {cruce && (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  <div style={{ border:"1px solid rgba(96,165,250,0.35)", borderRadius:10,
                    background:"rgba(96,165,250,0.05)", padding:"12px 14px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                      <i className="fa-solid fa-coins" style={{ color:"#60a5fa", fontSize:"0.7rem" }}/>
                      <span style={{ fontSize:"0.67rem", fontWeight:700, color:"var(--text-muted)",
                        letterSpacing:"0.6px", textTransform:"uppercase" }}>
                        {t("traslados.conversionMonedas")}
                      </span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                      <span style={{ fontSize:"0.8rem", color:"var(--text-muted)" }}>
                        {t("traslados.tipoCambio")}:
                      </span>
                      {loadingTC ? (
                        <i className="fa-solid fa-circle-notch fa-spin" style={{ color:"#60a5fa" }}/>
                      ) : (
                        <div style={{ display:"flex", alignItems:"center", gap:4,
                          background:"rgba(0,0,0,0.2)", border:"1px solid var(--border-card)",
                          borderRadius:7, padding:"4px 10px" }}>
                          <span style={{ fontSize:"0.8rem", color:"var(--text-muted)", fontWeight:600 }}>₡</span>
                          <input type="number" step="0.01" min="1.01"
                            value={tc}
                            onChange={e => set("tipo_cambio", parseFloat(e.target.value) || 1)}
                            style={{ width:90, background:"transparent", border:"none",
                              color:"var(--text-primary)", fontSize:"0.88rem", fontFamily:"monospace",
                              fontWeight:600, outline:"none", padding:0 }}/>
                          <span style={{ fontSize:"0.7rem", color:"var(--text-muted)" }}>/ USD</span>
                        </div>
                      )}
                    </div>
                    <FieldError msg={errors.tipo_cambio}/>
                  </div>

                  {/* Ajuste manual del monto que entra — el banco a veces redondea
                      distinto al TC exacto y hay que forzar el monto real reportado.
                      Tarjeta propia (no anidada dentro de la de TC) para no amontonar. */}
                  <div style={{ border:`1px solid ${ajusteManual ? "rgba(249,115,22,0.35)" : "var(--border-card)"}`,
                    borderRadius:10, background: ajusteManual ? "rgba(249,115,22,0.05)" : "var(--hover-bg)",
                    padding:"14px 16px", transition:"border-color 0.15s, background 0.15s" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:12 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <i className="fa-solid fa-sliders"
                          style={{ color: ajusteManual ? "#f97316" : "var(--text-muted)", fontSize:"0.7rem" }}/>
                        <span style={{ fontSize:"0.67rem", fontWeight:700,
                          color: ajusteManual ? "#f97316" : "var(--text-muted)",
                          letterSpacing:"0.6px", textTransform:"uppercase" }}>
                          {t("traslados.montoQueEntra")}
                        </span>
                      </div>
                      <button type="button"
                        onClick={() => {
                          if (!ajusteManual) setMontoDestinoAjust(String(montoDestinoCalc));
                          setAjusteManual(v => !v);
                        }}
                        style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 11px", borderRadius:7,
                          background: ajusteManual ? "rgba(249,115,22,0.15)" : "rgba(96,165,250,0.1)",
                          border: `1px solid ${ajusteManual ? "rgba(249,115,22,0.4)" : "rgba(96,165,250,0.3)"}`,
                          color: ajusteManual ? "#f97316" : "#60a5fa",
                          fontSize:"0.74rem", fontWeight:700, cursor:"pointer" }}>
                        <i className={`fa-solid ${ajusteManual ? "fa-rotate-left" : "fa-pen"}`} style={{ fontSize:"0.66rem" }}/>
                        {ajusteManual ? t("traslados.restaurarCalculado") : t("traslados.ajustarManual")}
                      </button>
                    </div>

                    {!ajusteManual ? (
                      <div style={{ display:"inline-flex", alignItems:"center", gap:6,
                        background:"rgba(0,0,0,0.2)", border:"1px solid var(--border-card)",
                        borderRadius:7, padding:"8px 12px" }}>
                        <span style={{ color:"var(--text-muted)", fontWeight:600, fontSize:"0.9rem" }}>
                          {destino ? sim(destino.moneda) : "₡"}
                        </span>
                        <span style={{ fontFamily:"monospace", fontWeight:700, fontSize:"1.05rem",
                          color:"var(--text-primary)" }}>
                          {fmtN(montoDestinoCalc)}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <IconInput icon="fa-pen" type="number" step="0.01" min="0.01"
                          value={montoDestinoAjust}
                          onChange={e => setMontoDestinoAjust(e.target.value)}
                          style={{ borderColor: errors.montoDestinoAjust ? "#ef4444" : "rgba(249,115,22,0.35)",
                            fontFamily:"monospace", fontWeight:700, fontSize:"1.02rem" }}/>
                        <FieldError msg={errors.montoDestinoAjust}/>
                        {montoDestinoAjustNum > 0 && Math.abs(diferenciaAjuste) > 0.001 && (
                          <p style={{ margin:"8px 0 0", fontSize:"0.73rem",
                            color: diferenciaAjuste >= 0 ? "#4ade80" : "#f87171" }}>
                            <i className={`fa-solid ${diferenciaAjuste >= 0 ? "fa-arrow-up" : "fa-arrow-down"}`}
                              style={{ marginRight:4 }}/>
                            {diferenciaAjuste >= 0 ? "+" : "-"}{sim(destino ? destino.moneda : "CRC")}{fmtN(Math.abs(diferenciaAjuste))}{" "}
                            {t("traslados.respectoCalculo")}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Detalle */}
              <div>
                <label>{t("traslados.detalleOpc")}</label>
                <IconTextarea icon="fa-file-lines"
                  placeholder={t("traslados.detallePlaceholder")}
                  value={form.detalle}
                  onChange={e => set("detalle", e.target.value)}/>
              </div>

            </div>
          </div>

          {/* ── Derecha: resumen ── */}
          <div className={addStyles["filial-details"]}>
            <div className={addStyles["filial-details-header"]}>
              <h1>
                <i className={`fa-solid fa-receipt ${addStyles["icon-filial-details"]}`}/>
                {t("registros.resumen")}
              </h1>
            </div>

            <div className={addStyles["invoice-details-body"]} style={{ marginTop:14 }}>
              <div className={addStyles["filial-details-summary"]}>
                <div className={addStyles["filial-summary-relevant"]}>
                  <h2>{t("registros.resumenRapido")}</h2>
                  {[
                    [t("common.fecha"),             form.fecha || "—"],
                    [t("traslados.origen"),         origen?.nombre || "—"],
                    [t("traslados.destino"),        destino?.nombre || "—"],
                    [t("common.referencia"),        form.referencia || "—"],
                  ].map(([label, val], i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      marginBottom:4, fontSize:14 }}>
                      <span style={{ color:"var(--text-muted)" }}>{label}</span>
                      <span style={{ color:"var(--text-secondary)", maxWidth:150,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap" }}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* Sale / Entra */}
                <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"9px 12px", borderRadius:7, background:"rgba(239,68,68,0.08)",
                    border:"1px solid rgba(239,68,68,0.25)" }}>
                    <span style={{ color:"#f87171", fontSize:13, fontWeight:600 }}>
                      <i className="fa-solid fa-arrow-up-from-bracket" style={{ marginRight:6 }}/>
                      {t("traslados.sale")}
                    </span>
                    <span style={{ color:"#f87171", fontFamily:"monospace", fontWeight:700 }}>
                      {origen ? sim(origen.moneda) : "₡"}{fmtN(montoNum)}
                    </span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"9px 12px", borderRadius:7, background:"rgba(74,222,128,0.08)",
                    border:"1px solid rgba(74,222,128,0.25)" }}>
                    <span style={{ color:"#4ade80", fontSize:13, fontWeight:600 }}>
                      <i className="fa-solid fa-arrow-down-to-bracket" style={{ marginRight:6 }}/>
                      {t("traslados.entra")}
                    </span>
                    <span style={{ color:"#4ade80", fontFamily:"monospace", fontWeight:700 }}>
                      {destino ? sim(destino.moneda) : "₡"}{fmtN(montoDestino)}
                    </span>
                  </div>
                </div>

                {/* Nota de conversión — si hay ajuste manual, muestra el TC EFECTIVO
                    implícito (no el nominal), ya que es lo que realmente se contabiliza */}
                {cruce && montoNum > 0 && (
                  <div style={{ marginTop:8, fontSize:"0.74rem", color:"var(--text-muted)",
                    textAlign:"center" }}>
                    {sim(origen.moneda)}{fmtN(montoNum)} → {sim(destino.moneda)}{fmtN(montoDestino)} {t("traslados.alTC")}{" "}
                    {(ajusteManual && montoDestino > 0
                      ? (origen.moneda === "CRC" ? montoNum / montoDestino : montoDestino / montoNum)
                      : tc
                    ).toFixed(2)}
                    {ajusteManual && (
                      <span style={{ color:"#f97316", fontWeight:600 }}> ({t("traslados.ajustado")})</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={addStyles["invoice-footer"]}>
            <div className={addStyles.actions}>
              <button type="button" onClick={onCancel}
                className={`${addStyles.btn} ${addStyles["btn-clean-form"]}`}>
                <i className="fa-solid fa-arrow-left" style={{ marginRight:6 }}/>
                {t("common.cancelar")}
              </button>
              <button type="submit" disabled={saving}
                className={`${addStyles.btn} ${addStyles["btn-save"]}`}
                style={{ opacity:saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("registros.guardando")}</>
                  : editMode
                    ? <><i className="fa-solid fa-floppy-disk"/> {t("traslados.guardarCambios")}</>
                    : <><i className="fa-solid fa-right-left"/> {t("traslados.guardarTraslado")}</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}

export default function AddTraslado() {
  const navigate = useNavigate();
  const { id } = useParams();
  return (
    <FormTraslado
      key={id || "nuevo"}
      trasladoId={id}
      onCancel={() => navigate("/finanzas/traslados")}
    />
  );
}
